import { Link } from "react-router-dom";

import { type Post, useAdminPosts, useDeletePost } from "@/lib/queries/use-posts";

function formatDate(dateString: string | null): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function PostRow({ post }: { post: Post }) {
  const deletePost = useDeletePost();

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${post.title}"?`)) {
      deletePost.mutate(post.id);
    }
  };

  return (
    <div className="p-4 md:py-4 md:px-0 border border-border md:border-0 md:border-b bg-card md:bg-transparent">
      {/* Mobile: Card layout, Desktop: Row layout */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <div className="flex-1">
          <div className="flex items-start md:items-center gap-2 flex-wrap">
            <Link
              to={`/admin/posts/${post.id}`}
              className="text-[14px] md:text-[15px] font-bold text-text-primary hover:text-primary transition-colors"
            >
              {post.title}
            </Link>
            <div className="flex gap-2">
              {!post.published && (
                <span className="text-[9px] md:text-[10px] text-text-faint tracking-caps uppercase px-1.5 md:px-2 py-0.5 border border-border">
                  Draft
                </span>
              )}
              {post.type === "note" && (
                <span className="text-[9px] md:text-[10px] text-text-faint tracking-caps uppercase px-1.5 md:px-2 py-0.5 border border-border">
                  Note
                </span>
              )}
            </div>
          </div>
          <p className="text-[11px] md:text-[12px] text-text-muted mt-1">
            {post.published
              ? `Published ${formatDate(post.published_at)}`
              : `Created ${formatDate(post.created_at)}`}
          </p>
        </div>
        <div className="flex items-center gap-4 pt-2 md:pt-0 border-t border-border md:border-0 mt-2 md:mt-0">
          <Link
            to={`/admin/posts/${post.id}`}
            className="text-[11px] md:text-[12px] text-text-muted hover:text-primary py-1"
          >
            Edit
          </Link>
          {post.published && (
            <Link
              to={`/writing/${post.slug}`}
              target="_blank"
              className="text-[11px] md:text-[12px] text-text-muted hover:text-primary py-1"
            >
              View
            </Link>
          )}
          <button
            onClick={handleDelete}
            className="text-[11px] md:text-[12px] text-text-muted hover:text-destructive py-1"
            disabled={deletePost.isPending}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminPostsList() {
  const { data: posts, isLoading, error } = useAdminPosts();

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Posts</h1>
        <Link
          to="/admin/posts/new"
          className="px-3 md:px-4 py-1.5 md:py-2 bg-primary text-primary-foreground font-bold text-[12px] md:text-[13px] hover:opacity-90 transition-opacity"
        >
          + New
        </Link>
      </div>

      {isLoading && <p className="text-text-muted py-8 text-center">Loading posts...</p>}

      {error && (
        <p className="text-destructive py-8 text-center">Failed to load posts. Please try again.</p>
      )}

      {posts && posts.length === 0 && (
        <div className="py-12 text-center border border-dashed border-border">
          <p className="text-text-muted mb-4">No posts yet</p>
          <Link to="/admin/posts/new" className="text-[13px] text-primary hover:underline">
            Create your first post
          </Link>
        </div>
      )}

      {posts && posts.length > 0 && (
        <div className="space-y-3 md:space-y-0 md:border-t md:border-border">
          {posts.map((post) => (
            <PostRow key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
}
